import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryOptions, useSuspenseQuery} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {ActivityResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/activity'
import type {ActivityPayload} from '../../../routes/route-payload-types'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {normalizeActivity, protoActivityToLegacy} from './activity-proto'
import {useEffectiveSelection} from './use-effective-selection'

export function useSuspenseActivityPageData() {
  const {data, ...rest} = useSuspenseQuery(useActivityQueryOptions())
  return {...data, ...rest}
}

function useActivityQueryOptions() {
  const {effectiveSelection, lastActivityTimestamp} = useEffectiveSelection()
  const queryKey = [PageData.activity, effectiveSelection?.timestamp, lastActivityTimestamp] as const

  const since = effectiveSelection?.timestamp ?? ''
  // Fold `since` into the path *before* `getInsightsUrl` runs (inside
  // `usePageDataUrl`) so insights query params (e.g. `_tracing`,
  // `disable_clusters`) and `since` merge into a single query string. Appending
  // `since` separately via `queryFnFetch` would emit a second `?` whenever an
  // insights query string is already present, silently dropping the required
  // `since` filter.
  const pathname = usePageDataUrl(PageData.activity, new URLSearchParams({since}).toString())

  return queryOptions({
    queryKey,
    // the since query param is required, so we can't enable the query if it's not set
    enabled: !!effectiveSelection,
    queryFn: () => fetchActivityPageData(pathname),
    staleTime: Infinity,
  })
}

async function fetchActivityPageData(pathname: string): Promise<ActivityPayload> {
  // `queryFnFetch` decodes proto-mode responses (flag on) via the schema; legacy
  // (flag off) JSON passes through and is converted up to the proto shape by
  // `normalizeActivity`, then rendered through the permanent domain converter.
  const result = await queryFnFetch<typeof ActivityResponseSchema, ActivityPayload>(
    {
      queryDeps: {pathname},
      routeId: 'pull-request-activity',
      queryName: 'activityPageData',
    },
    {schema: ActivityResponseSchema},
  )
  return protoActivityToLegacy(normalizeActivity(result))
}
